import React, { FC, useMemo } from 'react';

import {
  EntitySortableField,
  SortDir,
  UploadEntityMutationsCsvMutation as UploadEntityMutationsCsv,
  UploadEntityMutationsCsvMutationVariables as UploadEntityMutationsCsvVariables,
} from 'core/graphql';
import { SortDirection } from 'shared/components/data/Sort';
import { FileUpload } from 'shared/components/form/FileUpload';
import { GridHeader } from 'shared/components/internal/GridHeader';
import { SortMenu } from 'shared/components/internal/SortMenu';
import { useMutation } from 'shared/hooks';

import { UPLOAD_ENTITY_METADATA_MUTATION } from '../mutations';
import { sortOptions } from '../sort';
import { EntitiesSearchFilter } from './EntitiesSearchFilter';
import { EntityMetadataImportGuide } from './EntityMetadataImportGuide';

interface EntitiesHeaderProps {
  query: string;
  sort: EntitySortableField;
  sortDir: SortDir;
  total: number;
  onQueryChange: (query: string) => void;
  onSortChange?: (sorts: SortDirection<EntitySortableField>[]) => void;
}

export const EntitiesHeader: FC<
  React.PropsWithChildren<EntitiesHeaderProps>
> = ({ onQueryChange, onSortChange, query, sort, sortDir, total }) => {
  const menuSort = useMemo(() => {
    const result = Object.entries(sortOptions).find(
      ([_, { name }]) => name === sort
    );

    return result?.length ? result[0] : 'activityScore';
  }, [sort]);

  const [uploadEntities, { loading: uploading }] = useMutation<
    UploadEntityMutationsCsv,
    UploadEntityMutationsCsvVariables
  >(UPLOAD_ENTITY_METADATA_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['EntityListQueryOperation'],
  });

  const handleImport = async ({ addInfo, values }) => {
    const result = await uploadEntities({ variables: { file: values.file } });
    const { uploadEntityMutationsCsv: res } = result?.data ?? {};

    const itemCount = res?.uploadedEntitiesCount?.toLocaleString();
    const grammar = `${itemCount === '1' ? ' was' : 's were'}`;

    if (res && itemCount) {
      addInfo(
        `${itemCount} Entity configuration${grammar} imported in the background`
      );
    }
  };

  return (
    <GridHeader
      actions={
        <FileUpload
          header='Import Entity Metadata'
          intro={<EntityMetadataImportGuide />}
          onSubmit={handleImport}
          uploading={uploading}
        />
      }
      searchFilter={
        <EntitiesSearchFilter
          onQueryChange={onQueryChange}
          queryString={query}
        />
      }
      sortMenu={
        <SortMenu
          dataSumoTestPrefix='entities'
          onSortsChange={onSortChange}
          sort={menuSort}
          sortDir={sortDir === SortDir.Asc ? SortDir.Asc : SortDir.Desc}
          sortOptions={sortOptions}
        />
      }
      title='Entities'
      total={total}
    />
  );
};
